import {
    Button,
    Form,
    FormLayout,
    TextField,
    LegacyCard,
    EmptyState,
    IndexTable,
    useIndexResourceState,
    Link,
    Text,
    InlineStack,
    Select,
    Tooltip,
    Icon,
    BlockStack, Box
} from '@shopify/polaris';

import {useState, useEffect, useCallback, useMemo} from 'react';
import Api from "../../helpers/api";
import Paginator from "../../components/Paginator";
import {useToast} from "@shopify/app-bridge-react";
import OrderDetailsModal from "./OrderDetailsModal";
import {formatDateToUsFormat} from "../Dates";
import {useLocation} from "react-router-dom";
import ShippingExportModal from "./ShippingExportModal";
import {getOrderBadges, getFulfillmentBadge} from "./helpers";
import {NoteMajor, CircleAlertMajor} from "@shopify/polaris-icons"

const OrdersSearch = ({
    searchType,
    loading,
    setLoading,
    customerId,
    searchTitle,
    exportModalActive,
    setExportModalActive,
}) => {
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState({current_page: 1, last_page: 1, total: 0})
    const [errors, setErrors] = useState({})
    const toast = useToast();
    const [displayedOrder, setDisplayedOrder] = useState(null);
    const [config, setConfig] = useState({});

    const queryString = useQuery();
    const [query, setQuery] = useState(queryString.get('query') ?? '');
    const {selectedResources, allResourcesSelected, handleSelectionChange, clearSelection} = useIndexResourceState(results);
    const [paymentMethods, setPaymentMethods] = useState({});

    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('all');

    useEffect(() => {
        setLoading(true);

        Promise.all([
            Api.getConfig(),
            Api.listUsers(),
        ])
            .then((data) => {
                setConfig(data[0].data);
                setUsers([
                    {
                        label: 'All',
                        value: 'all',
                    },
                    ...data[1].data.map(item => ({
                        ...item,
                        value: item.value.toString(),
                    })),
                ]);
            })
            .finally(() => {
                setLoading(false);
            })
        Api.getConfig()
            .then(({data}) => {
                setConfig(data);
            });
    }, []);

    function useQuery() {
        const { search } = useLocation();

        return useMemo(() => new URLSearchParams(search), [search]);
    }

    useEffect(() => {
        clearSelection();
        loadOrders();
    }, [searchType, userId]);

    const handleSubmit = (search = null) => {
        clearSelection();
        loadOrders(1, search);
    }

    const loadOrders = useCallback((page = 1, search = null) => {
        setLoading(true);
        setResults([]);
        setMeta({current_page: 1, last_page: 1, total: 0});

        Api
            .findOrders({
                search: search ?? query,
                type: searchType,
                page: page,
                customer_id: customerId,
                user_id: userId,
            })
            .then((response) => {
                setErrors([]);
                setResults(response.data?.data ?? []);
                setMeta(response.data?.meta ?? {current_page: 1, last_page: 1, total: 0});
                setPaymentMethods(response.data?.payment_methods ?? {});
            })
            .catch(err => {
                if (err.response.status == 422) {
                    toast.show("Validation errors", {duration: 5000});
                    setErrors(
                        Object.keys(err.response.data.errors).reduce((prev, key) => {
                            prev[key] = err.response.data.errors[key][0];
                            return prev;
                        }, {}),
                    );
                } else if (err.response.status == 403) {
                    toast.show("You don't have permissions to view this page.", {duration: 5000});
                } else {
                    toast.show("Server error", {duration: 5000});
                }

            })
            .finally(() => setLoading(false));
    }, [query, searchType, userId]);

    const mergeOrders = useCallback(() => {
        if (selectedResources.length <= 1) {
            toast.show("You need to select at least 2 orders");
            return;
        }

        let ordersWithoutCustomer = [];
        let companies = [];
        let customers = [];
        let mergingOrders = [];

        const selectedMergedOrder = results
            .filter(order => {
                const included = selectedResources.includes(order.id);
                const skip = order.merged_into_order;

                if (included && !order.customer) {
                    ordersWithoutCustomer.push(order.id);

                    return false;
                }

                if (included && order.merging) {
                    mergingOrders.push(order.id);

                    return false;
                }

                //If order is ready to be merged, add it to companies or customers array to check for uniqueness
                if (included && !skip) {
                    if (order.customer?.company_name && !companies.includes(order.customer.company_name)) {
                        companies.push(order.customer.company_name);
                    } else if (!order.customer?.company_name && !customers.includes(order.customer.id)) {
                        customers.push(order.customer.id);
                    }
                }

                return included && skip;
            })
            .length;

        if (mergingOrders.length > 0) {
            toast.show("You can't merge Orders that are currently being merged with other orders.", {
                duration: 7000
            });
            return;
        }

        if (ordersWithoutCustomer.length > 0) {
            toast.show("You can't merge Orders that doesn't have customer.", {
                duration: 7000
            });
            return;
        }

        if (companies.length > 1 || (companies.length > 0 && customers.length > 0)) {
            toast.show("You can't merge Orders for different companies or for customers from different companies.", {
                duration: 7000
            });
            return;
        }

        if (selectedMergedOrder > 0) {
            toast.show("You can't merge Orders that have already been merged.", {
                duration: 7000
            });
            return;
        }

        const confirmation = confirm("Are you sure you want to merge selected Orders? This action cannot be undone.");

        if (confirmation) {
            setLoading(true);
            Api
                .mergeOrders(selectedResources)
                .then(() => {
                    toast.show("Orders merging has started");
                    clearSelection();
                    loadOrders(meta.current_page, query);
                })
                .catch(() => {
                    toast.show("Server error. Refresh the orders list and try again.");
                    setLoading(false);
                });

        }
    }, [selectedResources, query, results]);

    const submitOrders = useCallback(() => {
        if (selectedResources.length <= 1) {
            toast.show("You need to select at least 2 orders");
            return;
        }

        const confirmation = confirm("Are you sure you want to Submit selected Orders?");

        if (confirmation) {
            setLoading(true);
            Api
                .toggleOrdersSubmit(selectedResources)
                .then(() => {
                    toast.show("Orders submit has started");
                    clearSelection();
                    loadOrders(meta.current_page, query);
                })
                .catch(() => {
                    toast.show("Server error. Please try again.");
                    setLoading(false);
                });

        }
    }, [selectedResources, query]);

    return <BlockStack gap={'400'}>
        <Form onSubmit={() => handleSubmit()}>
            <FormLayout>
                <FormLayout>
                    <FormLayout.Group>
                        <TextField
                            value={query}
                            autoComplete={'on'}
                            onChange={setQuery}
                            label={<InlineStack gap={'200'}>
                                <Text as={'span'}>Search</Text>
                                <Tooltip content={searchTitle ?? "Search orders by Order Number, Customer Name, Customer Email, Customer Phone, Account Number or Company Name"}>
                                    <Icon source={CircleAlertMajor} />
                                </Tooltip>
                            </InlineStack>}
                            type="text"
                            error={errors['search']}
                            connectedRight={<Button submit variant="secondary">Search</Button>}
                            clearButton
                            onClearButtonClick={() => {setQuery(''); handleSubmit('')}}
                        />
                        <Select
                            label={'Sales Person'}
                            options={users}
                            value={userId}
                            onChange={(val) => setUserId(val)}
                        />
                    </FormLayout.Group>
                </FormLayout>
            </FormLayout>
        </Form>

        <style>{`
            /* Nudge the floating bulk-actions bar (Merge/Submit) up ~120px, orders table only */
            .orders-bulk-actions-scope .Polaris-IndexTable__BulkActionsWrapperSticky {
                bottom: calc(var(--p-space-1000) - var(--p-space-100) + 60px) !important;
            }
        `}</style>

        <BlockStack>
            { !loading && results.length > 0 && (
                <Paginator currentPage={meta.current_page} lastPage={meta.last_page} show={true} callback={loadOrders} />
            ) }
            <div className="orders-bulk-actions-scope">
            <LegacyCard>
                <IndexTable
                    promotedBulkActions={[
                        { content: "Merge Orders", onAction: mergeOrders,  disabled: loading},
                        { content: "Submit Orders", onAction: submitOrders,  disabled: loading}
                    ]}
                    emptyState={<EmptyState image={null}>There are no results matching your search.</EmptyState>}
                    loading={loading}
                    headings={[
                        {title: 'Order number'},
                        {title: ''},
                        {title: ''},
                        {title: 'Date'},
                        {title: 'Company name'},
                        {title: 'Merge Date'},
                        {title: 'Delivery Date Req'},
                        {title: 'Ship Date Set'},
                        {title: 'Sales Person'},
                        {title: 'Total'},
                    ]}
                    itemCount={meta.total}
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    onSelectionChange={handleSelectionChange}
                >
                    {results.map((order, index) => (<IndexTable.Row
                        id={order.id}
                        key={order.id}
                        selected={selectedResources.includes(order.id)}
                        position={index}
                    >
                        <IndexTable.Cell>
                            <InlineStack align={'space-between'}>
                                <Link removeUnderline onClick={() => {
                                    if (!loading) {
                                        setDisplayedOrder(order);
                                        clearSelection();
                                    }
                                }}>{order.additional_data.name}</Link>

                                {order.additional_data.note && (
                                    <Tooltip content={order.additional_data.note}>
                                        <Icon source={NoteMajor} />
                                    </Tooltip>
                                )}
                            </InlineStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            {getOrderBadges(order)}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            {getFulfillmentBadge(order)}
                        </IndexTable.Cell>
                        <IndexTable.Cell>{order.shopify_created_at_formatted}</IndexTable.Cell>
                        <IndexTable.Cell>{order.company?.name}</IndexTable.Cell>
                        <IndexTable.Cell>{order.merged_at_formatted}</IndexTable.Cell>
                        <IndexTable.Cell>{order.order_processing_form?.requested_delivery_date}</IndexTable.Cell>
                        <IndexTable.Cell>{order.pack_date_formatted}</IndexTable.Cell>
                        <IndexTable.Cell>{order.sales_rep?.name}</IndexTable.Cell>
                        <IndexTable.Cell>${(order?.is_cancelled ? order?.additional_data?.total_price : order?.additional_data?.current_total_price) ?? '0.00'}</IndexTable.Cell>
                    </IndexTable.Row>))}
                </IndexTable>
            </LegacyCard>
            </div>

            { !loading && results.length > 0 && (
                <Paginator currentPage={meta.current_page} lastPage={meta.last_page} show={true} callback={loadOrders} />
            ) }
        </BlockStack>

        <OrderDetailsModal
            order={displayedOrder}
            setDisplayedOrder={setDisplayedOrder}
            onClose={() => {
                clearSelection();
                setDisplayedOrder(null);
            }}
            onOrderSave={() => {
                clearSelection();
                loadOrders(meta.current_page, query);
            }}
            config={config}
            paymentMethods={paymentMethods}
        />

        <ShippingExportModal
            active={exportModalActive}
            onClose={() => setExportModalActive(false)}
        />
    </BlockStack>;
}

export default OrdersSearch;
